import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/use-auth';
import { safeRedirect } from '@/lib/safe-redirect';
import { recordSignupFreeIntent } from '@/api/billing';
import { isNewAccount } from '@/lib/oauth-redirect';

const searchSchema = z.object({
  // Signup attribution code carried across the OAuth round-trip on redirectTo.
  ref: z.string().optional(),
  // Ad click ids carried the same way; recorded into user_signup_params.
  // wbraid/gbraid replace gclid on iOS, so all four are carried.
  fbclid: z.string().optional(),
  gclid: z.string().optional(),
  wbraid: z.string().optional(),
  gbraid: z.string().optional(),
  // The email a GHL funnel landed them with, carried the same way. Named
  // `funnel_email` rather than `email` so it can never be mistaken for the
  // authenticated address — on this path the two are precisely what differ.
  funnel_email: z.string().optional(),
  // The "free via link" course's ClickUp id, carried the same way.
  course: z.string().optional(),
  // Onward destination after the callback resolves.
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackPage,
  validateSearch: searchSchema,
});

/**
 * OAuth return handler. Supabase can't carry custom data through the provider
 * round-trip, so a signup attribution `ref` rides the redirectTo query string
 * and is recorded here once the session is established. Also serves as the plain
 * post-OAuth landing (no ref) — e.g. the existing Facebook flow.
 */
function AuthCallbackPage() {
  const { user } = useAuth();
  const search = useSearch({ from: '/auth/callback' });
  const ref = search.ref?.trim().slice(0, 100) || undefined;
  const fbclid = search.fbclid?.trim().slice(0, 500) || undefined;
  const gclid = search.gclid?.trim().slice(0, 500) || undefined;
  const wbraid = search.wbraid?.trim().slice(0, 500) || undefined;
  const gbraid = search.gbraid?.trim().slice(0, 500) || undefined;
  const funnelEmail = search.funnel_email?.trim().slice(0, 320) || undefined;
  const course = search.course?.trim().slice(0, 64) || undefined;
  // Only ever a page on this site — see lib/safe-redirect.ts.
  const dest = safeRedirect(search.redirect) ?? '/';
  const ranRef = useRef(false);

  useEffect(() => {
    // Wait until supabase-js has exchanged the PKCE code and a session exists —
    // rpc_record_signup_ref reads auth.uid(). AuthContext sets `user` from
    // onAuthStateChange once the session is established.
    if (!user || ranRef.current) return;
    ranRef.current = true;
    void (async () => {
      if (ref) {
        try {
          // Best-effort; server-side stamp is idempotent + first-touch.
          const { error } = await supabase.rpc('rpc_record_signup_ref', { p_ref: ref });
          if (error) console.error('auth/callback: rpc_record_signup_ref failed', error);
        } catch (err) {
          console.error('auth/callback: rpc_record_signup_ref failed', err);
        }
      }
      const params: Record<string, string> = {};
      if (fbclid) params.fbclid = fbclid;
      if (gclid) params.gclid = gclid;
      if (wbraid) params.wbraid = wbraid;
      if (gbraid) params.gbraid = gbraid;
      if (funnelEmail) params.funnel_email = funnelEmail;
      if (course) params.course = course;
      if (Object.keys(params).length > 0) {
        try {
          // Best-effort; server-side writer allowlists keys + is first-touch.
          const { error } = await supabase.rpc('rpc_record_signup_params', { p_params: params });
          if (error) console.error('auth/callback: rpc_record_signup_params failed', error);
        } catch (err) {
          console.error('auth/callback: rpc_record_signup_params failed', err);
        }
      }
      // A free-course lead who signed up with Google/Microsoft/Facebook: record
      // their Free pick now. OAuth accounts arrive confirmed, so this queues
      // 'alfie free' (with the click id saved just above) straight away. Without
      // it a lead who never sets up a business never reaches GoHighLevel — the
      // email path does this at confirmation instead (routes/auth.confirm.tsx).
      if (course && user.email && isNewAccount(user.created_at)) {
        try {
          // Capped like the email path: a slow call must never strand them here.
          await Promise.race([
            recordSignupFreeIntent(user.id, user.email),
            new Promise((r) => setTimeout(r, 5000)),
          ]);
        } catch (err) {
          console.error('auth/callback: recordSignupFreeIntent failed', err);
        }
      }
      window.location.href = dest;
    })();
  }, [user, ref, fbclid, gclid, wbraid, gbraid, funnelEmail, course, dest]);

  // Fallback: if no session materialises (e.g. the provider errored), don't hang.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!ranRef.current) window.location.href = dest;
    }, 10000);
    return () => clearTimeout(t);
  }, [dest]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-muted-foreground text-sm">Finishing sign-in…</p>
    </div>
  );
}
